import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useEntities, useInvalidate } from "@/lib/queries";
import { EntityMultiSelect } from "@/components/EntityMultiSelect";
import { MergePreview } from "@/components/MergePreview";

export const MergeDialog = ({ open, onOpenChange, uid, targetId: initialTarget, onMerged }) => {
  const invalidate = useInvalidate();
  const { data: entities = [] } = useEntities(uid);
  const [targetId, setTargetId] = useState(initialTarget || "");
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState(false);
  const target = entities.find((e) => e.id === targetId);
  const [order, setOrder] = useState({ ids: [], ai: false, loading: false });
  const drag = useRef(null);
  const [step, setStep] = useState("pick");
  const [final, setFinal] = useState({ content: null, loading: true });
  const key = [...sources].sort().join(",");
  useEffect(() => {
    if (!targetId || sources.length < 2) { setOrder({ ids: sources, ai: false, loading: false }); return undefined; }
    let live = true;
    setOrder((o) => ({ ...o, ids: sources, loading: true }));
    const t = setTimeout(() => api.post(`/universes/${uid}/entities/merge/order`, { target_id: targetId, source_ids: sources })
      .then(({ data }) => live && setOrder({ ids: data.order, ai: data.ai, loading: false }))
      .catch(() => live && setOrder({ ids: sources, ai: false, loading: false })), 500);
    return () => { live = false; clearTimeout(t); };
  }, [key, targetId, uid]); // eslint-disable-line react-hooks/exhaustive-deps
  const move = (from, to) => setOrder((o) => {
    if (to < 0 || to >= o.ids.length || from === to) return o;
    const ids = [...o.ids]; const [x] = ids.splice(from, 1); ids.splice(to, 0, x);
    return { ...o, ids, ai: false, edited: true };
  });
  const name = (id) => entities.find((e) => e.id === id)?.name || "…";

  const ids = order.ids.length === sources.length ? order.ids : sources;
  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/universes/${uid}/entities/merge`, { target_id: targetId, source_ids: ids, content: final.content });
      invalidate(["entities", uid], "entity", "events", "pins", "regions", ["jumps", uid], ["suggestions", uid]);
      setTimeout(() => invalidate(["entities", uid], ["entity", data.id]), 30000);
      toast.success(`Merged ${data.merged.join(", ")} into ${target.name}`);
      onMerged?.(data.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-h-[92vh] overflow-y-auto ${step === "preview" ? "max-w-3xl" : "max-w-xl"}`} data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Merge entries</DialogTitle>
          <DialogDescription>Combine several entries into one. Their notes, attributes, links, events, map pins and jumps move to the entry you keep; their names become aliases.</DialogDescription>
        </DialogHeader>
        {step === "pick" ? (<>
        <div className="space-y-2">
          <Label>Keep this entry</Label>
          <Select value={targetId} onValueChange={(v) => { setTargetId(v); setSources(sources.filter((s) => s !== v)); }}>
            <SelectTrigger data-testid="merge-target-select"><SelectValue placeholder="Choose the entry to keep" /></SelectTrigger>
            <SelectContent>{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Merge into it (these will be removed)</Label>
          <EntityMultiSelect entities={entities.filter((e) => e.id !== targetId)} value={sources} onChange={setSources} testid="merge-sources" />
        </div>
        {sources.length > 1 && (
          <div className="space-y-2" data-testid="merge-order">
            <Label className="flex items-center gap-2">Order of merged notes
              {order.loading ? <span className="flex items-center gap-1 text-xs font-normal text-[#A8A090]"><Loader2 className="h-3 w-3 animate-spin" />Working out the chronological order…</span>
                : order.ai ? <span className="flex items-center gap-1 text-xs font-normal text-[#F59E0B]" data-testid="merge-order-ai"><Sparkles className="h-3 w-3" />Suggested chronological order</span>
                  : order.edited ? <span className="text-xs font-normal text-[#A8A090]">Your order</span> : null}
            </Label>
            <p className="text-xs text-[#A8A090]">{target?.name || "The kept entry"}'s own notes stay first. Each merged entry follows under its own title as a heading. Drag or use the arrows to reorder.</p>
            <ol className="space-y-1.5">
              {order.ids.map((id, i) => (
                <li key={id} draggable onDragStart={() => { drag.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => { move(drag.current, i); drag.current = null; }}
                  className="flex items-center gap-2 rounded-md border border-[#2D3442] bg-[#0F131A] px-2 py-1.5 text-sm text-[#F3EBDD]" data-testid={`merge-order-item-${id}`}>
                  <GripVertical className="h-4 w-4 cursor-grab text-[#6B6558]" />
                  <span className="w-5 text-xs text-[#A8A090]">{i + 1}.</span>
                  <span className="min-w-0 flex-1 truncate">{name(id)}</span>
                  <button onClick={() => move(i, i - 1)} disabled={i === 0} className="rounded p-1 text-[#A8A090] hover:text-[#F59E0B] disabled:opacity-30" aria-label="Move up" data-testid={`merge-order-up-${id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(i, i + 1)} disabled={i === order.ids.length - 1} className="rounded p-1 text-[#A8A090] hover:text-[#F59E0B] disabled:opacity-30" aria-label="Move down" data-testid={`merge-order-down-${id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ol>
          </div>
        )}
        </>) : <MergePreview uid={uid} targetId={targetId} ids={ids} onFinal={(content, loading) => setFinal({ content, loading })} />}
        <DialogFooter>
          {step === "pick" ? <>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="merge-cancel-button">Cancel</Button>
            <Button onClick={() => { setFinal({ content: null, loading: true }); setStep("preview"); }} disabled={!target || !sources.length || order.loading} data-testid="merge-preview-button">Preview merge</Button>
          </> : <>
            <Button variant="ghost" onClick={() => setStep("pick")} disabled={busy} data-testid="merge-back-button">Back</Button>
            <Button onClick={merge} disabled={busy || final.loading} data-testid="merge-confirm-button">
              {busy ? "Merging…" : `Merge ${sources.length} into ${target?.name || "…"}`}
            </Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
